import {Slider} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {ThemeProvider, useTheme} from 'styled-components';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

interface ChoiceSliderProps extends TestIdProps {
  label: string;
  choices: number[];
  emphasizedChoices?: number[];
  value: number;
  step?: number;
  formatChoice: (value: number) => string;
  formatAmount?: (value: number) => string;
  /**
   * Shown beside the label for the current value, so the ticks can stay short. It replaces the
   * floating thumb label, so a drag never shows the same value twice.
   */
  formatValue?: (value: number) => string;
  isDisabled?: boolean;
  isDiscrete?: boolean;
  /** Every position the thumb passes through, including the released one. */
  onDrag?: (value: number) => void;
  onChangeEnd: (value: number) => void;
}

/** The thumb follows a drag locally; `onDrag` reports each position, `onChangeEnd` the release. */
export function ChoiceSlider(props: ChoiceSliderProps) {
  const theme = useTheme();
  const [draggedValue, setDraggedValue] = useState<number | null>(null);
  const min = props.choices.at(0) ?? 0;
  const max = props.choices.at(-1) ?? min;
  const value = draggedValue ?? props.value;

  // Platform Slider fills its track with general.accent; scoped here so app links and focus rings keep theirs.
  const sliderTheme = {
    ...theme,
    colors: {...theme.colors, general: {...theme.colors.general, accent: theme.colors.accent.base}},
  };

  const ticks = props.choices.map((choice) => ({
    value: choice,
    label: props.formatChoice(choice),
    small: isNotNil(props.emphasizedChoices) && !props.emphasizedChoices.includes(choice),
  }));

  const onChange = (nextValue: number) => {
    if (props.isDiscrete && !props.choices.includes(nextValue)) {
      return;
    }
    setDraggedValue(nextValue);
    props.onDrag?.(nextValue);
  };

  const onChangeEnd = (releasedValue: number) => {
    if (props.isDiscrete && !props.choices.includes(releasedValue)) {
      return;
    }
    setDraggedValue(null);
    props.onChangeEnd(releasedValue);
  };

  return (
    <VStack spacing={2} data-testid={suffixTestId('choice', props)}>
      <HStack justify="space-between" align="baseline" spacing={3}>
        <span data-testid={suffixTestId('label', props)}>
          <Text size="base" color="secondary">
            {props.label}
          </Text>
        </span>
        <Show when={props.formatValue}>
          {(formatValue) => (
            <span data-testid={suffixTestId('value', props)}>
              <Text size="base" alternative>
                {formatValue(value)}
              </Text>
            </span>
          )}
        </Show>
      </HStack>
      <ThemeProvider theme={sliderTheme}>
        <Slider
          min={min}
          max={max === min && !props.isDiscrete ? min + 1 : max}
          step={props.isDiscrete ? props.choices : (props.step ?? props.choices)}
          ticks={ticks}
          nonlinearTicks={props.isDiscrete ? ticks : undefined}
          value={value}
          isDisabled={props.isDisabled || props.choices.length < (props.isDiscrete ? 1 : 2)}
          displayValue={isNil(props.formatValue) ? 'auto' : 'off'}
          formatValue={props.formatAmount ?? props.formatChoice}
          onChange={onChange}
          onChangeEnd={onChangeEnd}
          data-testid={props['data-testid']}
        />
      </ThemeProvider>
    </VStack>
  );
}
